import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import { useReduxLoading } from '@core/hooks';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import cancelarAlert from 'app/services/alerts/cancelarAlert';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect } from 'react';
import { FaCreditCard } from 'react-icons/fa';
import * as Yup from 'yup';
import { ContaContext } from '../../../ContaContext';
import HistoricoConta from '../../HistoricoConta';
import { modaisConta } from '../modais';
import { dataEncerramentoMenorQueDataSaldo } from '../validation/contaValidation';
import EditarContaCaixinhaForm from './EditarContaCaixinhaForm';

const isValidDateOrEmpty = (date) => {
  if (date && date.isValid()) {
    return true;
  }
  if (!date) {
    return true;
  }
  return false;
};

const EditarContaCaixinhaModal = ({ onSubmit }) => {
  const [modal, setModal] = useContext(ContaContext).modal;
  const [conta] = useContext(ContaContext).contaAtual;
  const [historico] = useContext(ContaContext).historico;
  const [loading, setLoading] = useContext(ContaContext).loading;
  const [page] = useContext(ContaContext).page;
  const [size] = useContext(ContaContext).size;

  useReduxLoading(loading);

  const handleSubmit = async (data) => {
    if (!formik.dirty) {
      handleClose();
    } else {
      setLoading(true);
      const _conta = {
        ...data,
        contaCaixinha: {
          ...data?.contaCaixinha,
          dataSaldo: data?.contaCaixinha?.dataSaldo ? data?.contaCaixinha?.dataSaldo.toISOString() : null,
          dataEncerramento: data?.contaCaixinha?.encerrarConta
            ? data?.contaCaixinha?.dataEncerramento.toISOString()
            : null,
        },
      };

      _conta.id = conta?.id;
      _conta.contaCaixinha.id = conta?.contaCaixinha?.id;

      const res = await onSubmit(_conta, { page, size });

      setLoading(false);
      if (res) {
        setModal('');
      }
    }
  };

  const initialValues = {
    descricao: conta.descricao || '',
    contaCaixinha: {
      saldoInicial:
        conta?.contaCaixinha?.saldoInicial !== null && conta?.contaCaixinha?.saldoInicial !== undefined
          ? conta?.contaCaixinha?.saldoInicial
          : '',
      dataSaldo: conta?.contaCaixinha?.dataSaldo ? moment(conta?.contaCaixinha?.dataSaldo) : null,
      encerrarConta: !!conta?.contaCaixinha?.dataEncerramento,
      dataEncerramento: conta?.contaCaixinha?.dataEncerramento ? moment(conta?.contaCaixinha?.dataEncerramento) : null,
    },
  };

  const initialErrors = {
    descricao: false,
    contaCaixinha: {
      saldoInicial: false,
      dataSaldo: false,
      encerrarConta: false,
      dataEncerramento: false,
    },
  };

  const contaCaixinhaSchema = Yup.object().shape({
    descricao: Yup.string()
      .required(mensagemCampoObrigatorio('a descrição'))
      .max(30, 'A descrição não pode ultrapassar 30 caracteres')
      .min(3, 'É necessário informar pelo menos 3 caracteres no campo'),
    contaCaixinha: Yup.object().shape({
      saldoInicial: Yup.number().required(mensagemCampoObrigatorio('o saldo inicial')),
      dataSaldo: Yup.object()
        .nullable()
        .test('testDate', 'Data inválida', isValidDateOrEmpty)
        .required(mensagemCampoObrigatorio('a data do saldo')),
      encerrarConta: Yup.bool().default(false),
      dataEncerramento: Yup.object()
        .nullable()
        .test('dataEncerramento', mensagemCampoObrigatorio('a data do encerramento', true), (dataEncerramento) => {
          if (formik.values?.contaCaixinha?.encerrarConta) {
            return !!dataEncerramento && isValidDateOrEmpty(dataEncerramento);
          }
          return true;
        })
        .test(
          'dataEncerramentoMaiorQueDataSaldo',
          'A data de encerramento deve ser igual ou maior que a data do saldo inicial',
          function(dataEncerramento) {
            return dataEncerramentoMenorQueDataSaldo(dataEncerramento, this.parent.dataSaldo);
          }
        ),
    }),
  });

  const formik = useFormik({
    onSubmit: handleSubmit,
    validationSchema: contaCaixinhaSchema,
    validateOnBlur: true,
    validateOnChange: true,
    initialValues,
    initialErrors,
  });

  useEffect(() => {
    formik.resetForm(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conta]);

  const handleClose = () => {
    const close = () => {
      formik.resetForm(initialValues);
      setModal('');
    };

    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const Buttons = (
    <>
      <CancelButton title="cancelar" type="button" onClick={handleClose}>
        Cancelar
      </CancelButton>
      <SaveButton color="buttonSave" type="button" onClick={formik.handleSubmit}>
        Salvar
      </SaveButton>
    </>
  );

  return (
    <FormikProvider value={formik}>
      <SimpleDialog fullScreen open={modal === modaisConta.editarContaCaixinha} onClose={handleClose}>
        <SimpleDialogContent
          fullScreen
          header={{ title: 'Cadastro de conta caixinha', icon: <FaCreditCard />, toolbar: Buttons }}
          content={
            <>
              <div style={{ padding: 16 }}>
                <EditarContaCaixinhaForm />
              </div>
              {conta?.id && historico.length > 0 && <HistoricoConta historico={historico} />}
            </>
          }
        />
      </SimpleDialog>
    </FormikProvider>
  );
};

export default EditarContaCaixinhaModal;
